import { recordError } from "./errorReporting"

export type ResponseParseOptions = {
  report4xxErrors?: boolean
}

const defaultResponseParseOptions: ResponseParseOptions = {
  report4xxErrors: true,
}

export function formatResponseSummary(response: Response, body: string) {
  return `url="${response.url}", status=${response.status}, statusText="${response.statusText}", body="${body}"`
}

async function tryParseJson<T>(response: Response): Promise<T | null> {
  const body = await response.text()

  try {
    return JSON.parse(body) as T
  } catch (error) {
    recordError(`Failed to parse response body as JSON: ${formatResponseSummary(response, body)}`, error)
    return null
  }
}

function reportNotOk(response: Response, options: ResponseParseOptions, message: string) {
  if (!options.report4xxErrors && response.status >= 400 && response.status < 500) {
    console.error(message)
  } else {
    recordError(message)
  }
}

export async function parseJsonOrThrow<T>(response: Response, options = defaultResponseParseOptions): Promise<T> {
  if (!response.ok) {
    const body = await response.text()
    const message = formatResponseSummary(response, body)

    reportNotOk(response, options, message)

    throw new Error(message)
  }

  const data = await tryParseJson<T>(response)

  if (!data) {
    throw new Error("Failed to parse response body as JSON")
  }

  return data
}

export async function parseJsonOrNull<T>(response: Response, options = defaultResponseParseOptions): Promise<T | null> {
  if (response.status === 204 || response.status === 404) {
    return null
  }

  if (!response.ok) {
    const body = await response.text()
    const message = formatResponseSummary(response, body)

    reportNotOk(response, options, message)

    return null
  }

  return tryParseJson<T>(response)
}

export async function tryFetch(url: string, options: RequestInit = {}) {
  try {
    return await fetch(url, options)
  } catch (error) {
    const method = options.method || "GET"
    recordError(`Fetch error: ${method} ${url}`, error)
    return new Response(null, { status: 500, statusText: "Fetch error", headers: {} })
  }
}
